import { IApiError, IApiErrorPayloadWithCode } from 'types';

interface IApiTransformVars {
  [key: string]: string;
}

const apiVariables: IApiTransformVars = {
  verify_code: 'Verification Code',
  existing_password: 'Current password',
  organization_id: 'Organization id',
  invite_recipient: 'Invite recipient',
  date_of_birth: 'Date of birth',
  user: 'Employee',
  name: 'Name',
  resource: 'This resource',
};

const apiErrorMessage: IApiTransformVars = {
  'invalid code': 'invalid. Please try again or request a new code.',
  unauthorized: 'Email and password combination incorrect',
  Unauthorized: 'Email and password combination incorrect',
  'invalid user password': 'is incorrect',
  'an employee already exists for this user': 'already exists for this user',
  'Employee is already associated to the site': 'already has this employee associated with it',
  'Insufficient funds for site': 'Sorry, this site currently has insufficient funds to fulfill your request. Please contact your organization administrator or Kickfin for assistance.',
};

interface IApiErrorWithCode {
  errors: IApiError[];
  statusCode?: string;
  requestMethod?: string;
  requestName?: string;
}

interface IApiCustomMessagesType {
  param: string;
  msg: string;
  customMsg: string;
}

const apiCustomMessages: IApiCustomMessagesType[] = [
  {
    param: 'identity',
    msg: 'not found',
    customMsg: 'This combination of contact information and date of birth does not match any account on file.',
  },
  {
    param: 'identity',
    msg: 'already exists',
    customMsg: 'This contact is already in use on another account.',
  },
];

const getCustomErrorMessage = (param: string, msg: string, statusCode: number, remaining_attempts: number) => {
  let customMsg = '';

  if (statusCode === 423) {
    customMsg = 'This account is temporarily locked due to too many failed login attempts. Please try again later.';
  }

  if (!customMsg && remaining_attempts && remaining_attempts < 4) {
    customMsg = `Email and password combination incorrect. You have ${remaining_attempts} attempt${remaining_attempts > 1 ? 's' : ''} remaining before your account is locked.`;
  }

  if (!customMsg) {
    customMsg = apiCustomMessages.find((apiCustomMessage) => apiCustomMessage.param === param && apiCustomMessage.msg === msg)?.customMsg;
  }

  return customMsg;
};

/**
 * This function formats error parameters, messages and values returned from the api and transforms them into predefined error messages
 * @param {IApiErrorWithCode} apiResponse - The response object containing:
 *    - `errors`: An array of error objects.
 *    - `statusCode`: The error's status code.
 *    - `requestMethod`: HTTP request method of the request that triggered the error.
 *    - `requestName`: Name of the request that triggered the error.
 * @returns {IApiErrorPayloadWithCode} - An object containing:
 *    - `errorMessages`: An array of formatted error messages. Each message combines the transformed `param` and `msg` from the input `apiResponse.errors`.
 *                       If the function encounters an error, it logs the error and returns a default error message.
 *    - `id`: The ID associated with the first error in the input `apiResponse.errors`. If the function encounters an error, the requestMethod and the requestName are returned.
 *    - `statusCode`: The status code from the input `apiResponse` as a string. If the function encounters an error, it returns 500
 */

export const formatErrorMessages = (apiResponse: IApiErrorWithCode): IApiErrorPayloadWithCode => {
  try {
    const apiErrors = apiResponse.errors.map(({ param, value, msg, remainingAttempts }: IApiError) => {
      const customMsg = getCustomErrorMessage(param, msg, parseInt(apiResponse.statusCode, 10), remainingAttempts);
      const transformedParam = param ? apiVariables[param] || param : '';
      const transformedMsg = msg ? apiErrorMessage[msg] || msg : '';
      return customMsg || `${transformedParam}${value ? ` ${value} ` : ' '}${transformedMsg}`;
    });
    return {
      errorMessages: apiErrors.join('').trim() ? apiErrors : ['An error occurred. Please try again later.'],
      id: apiResponse.errors.find((err) => err.id)?.id,
      statusCode: apiResponse.statusCode?.toString(),
    };
  } catch (err) {
    return {
      errorMessages: ['An error occurred. Please try again later.'],
      id: `${apiResponse?.requestMethod}_${apiResponse?.requestName}`,
      statusCode: apiResponse?.statusCode || '500',
    };
  }
};
